import { useEffect, useId, useMemo, useRef } from "react";
import * as machines from "@sablier/v2-machines";
import { _ } from "@sablier/v2-mixins";
import { IProvider, IToken } from "@sablier/v2-types";
import { useMachine } from "@xstate/react";

interface Props {
  chainId?: number;
  owner?: string;
  provider?: IProvider;
  token?: IToken;
}

export default function useTokenBalance({
  chainId,
  owner,
  provider,
  token,
}: Props) {
  const id = useId();
  const reference = useRef(machines.balance.create({ id }));
  const [machine, send] = useMachine(reference.current);

  const error = useMemo(() => machine.context.error, [machine.context.error]);
  const state = useMemo(() => machine.value, [machine.value]);
  const value = useMemo(
    () =>
      _.toValue({
        raw: machine.context.balance,
        decimals: token?.decimals,
      }),
    [machine.context.balance, token],
  );

  const isLoading = useMemo(
    () => ["checking"].includes(state as string),
    [state],
  );

  /**
   *
   * LOGGING
   *
   */

  useEffect(() => {
    (async () => {
      console.info(
        "%c[balance]",
        "color: green",
        "[async]",
        _.toString(token?.address || "").substring(0, 5) || undefined,
        _.toString(owner || "").substring(0, 5) || undefined,
        value.humanized.toString() || undefined,
      );
    })();
  }, [owner, token, value]);

  useEffect(() => {
    console.info("%c[balance]", "color: green", "[state]", state ?? "");
  }, [state]);

  /**
   *
   * AUTOMATED SIDE-EFFECTS
   *
   */

  /** If dependencies change (either account or token address) the machine has to be reset */
  useEffect(() => {
    send("RESET");
  }, [chainId, send, owner, provider, token]);

  /** If dependencies are in order, start transitioning through the states */
  useEffect(() => {
    (async () => {
      if (_.isNil(provider) || _.isNil(token) || _.isNilOrEmptyString(owner)) {
        return;
      }

      if (state === "idle") {
        send("CHECK", {
          payload: {
            owner,
            provider,
            token,
          },
        });
      }
    })();
  }, [owner, provider, send, state, token]);

  return {
    ...value,
    error,
    isLoading,
  };
}
